import {
  Discount,
  ResponseOrderWithCartLines,
} from "~/entities/orderEntities";

// Pure display aggregation for BUNDLE_PRICE deal rows (deep-dive §4.5):
// components render at full price and the cart shows ONE aggregated green row
// per bundle promotion code. Amounts are computed from the line records
// themselves — never from the totals subtraction.
export interface BundleDealRow {
  promotionCode: string;
  label: string;
  // Total impact of this bundle code, as a NEGATIVE integer in cents —
  // callers pass it straight to formatAmount (which renders the sign).
  amount: number;
}

const BUNDLE_PRICE = "BUNDLE_PRICE";

// One tag per bundle a line belongs to: the line states membership (the deal
// NAME only); the aggregated deal row remains the single place the saving is
// shown. Both record surfaces are read because ROOT_ITEM-style records live
// on item.discounts.
export interface LineBundleTag {
  promotionCode: string;
  name: string;
}

type LineWithDiscounts = {
  discounts?: Discount[] | null;
  item: { discounts?: Discount[] | null };
};

const lineRecords = (line: LineWithDiscounts): Discount[] => [
  ...(line.discounts ?? []),
  ...(line.item.discounts ?? []),
];

export const lineBundleTags = (line: LineWithDiscounts): LineBundleTag[] => {
  const seen = new Map<string, string>();
  for (const record of lineRecords(line)) {
    if (
      record.discountType === BUNDLE_PRICE &&
      !seen.has(record.promotionCode)
    ) {
      seen.set(record.promotionCode, record.name || record.promotionCode);
    }
  }
  return [...seen.entries()].map(([promotionCode, name]) => ({
    promotionCode,
    name,
  }));
};

// True when every discount on the line is a bundle record — such lines render
// at FULL price (spec §6: components at full price; the deal row carries the
// saving). Lines mixing bundle + ordinary discounts keep the strikethrough.
export const lineHasOnlyBundleDiscounts = (
  line: LineWithDiscounts,
): boolean => {
  const records = lineRecords(line);
  return (
    records.length > 0 &&
    records.every((record) => record.discountType === BUNDLE_PRICE)
  );
};

export const bundleDealRows = (
  orderSummary: ResponseOrderWithCartLines
): BundleDealRow[] => {
  const groups = new Map<string, { label: string; total: number }>();

  for (const line of orderSummary.lines) {
    const qty = line.item.qty;
    // The server attaches bundle records at LINE level (line.discounts), but
    // include item-level records defensively — CartItem reads both surfaces.
    const records: Discount[] = [
      ...(line.discounts ?? []),
      ...(line.item.discounts ?? []),
    ];
    for (const record of records) {
      if (record.discountType !== BUNDLE_PRICE) {
        continue;
      }
      // Line-level fixedAmount is PER-UNIT; money impact = fixedAmount × qty.
      const impact = (record.fixedAmount ?? 0) * qty;
      const existing = groups.get(record.promotionCode);
      if (existing) {
        existing.total += impact;
      } else {
        groups.set(record.promotionCode, {
          label: record.name || record.promotionCode,
          total: impact,
        });
      }
    }
  }

  return [...groups.entries()]
    .filter(([, group]) => group.total !== 0)
    .map(([promotionCode, group]) => ({
      promotionCode,
      label: group.label,
      amount: -group.total,
    }))
    .sort((a, b) => a.label.localeCompare(b.label));
};
